import { InputLabel } from './common';
import { Horizontal } from 'packages/radiant-react/layout';
import styles from './styles.module.scss';
import { AddFontModal } from '../Modals';
import React, { useEffect, useState } from 'react';
import { useStyleContext, CustomFont } from '../../../context/StyleContext';
import type { SelectOption } from 'packages/radiant-react/select/base-select';
import { Colors, Typography } from 'packages/radiant-react/typography';
import { Select } from 'packages/radiant-react/select';
import { getInfoObject } from 'utils/tokenService';

interface FontInputProps {
  onChange: (value: string) => void;
  options?: { value: string; label: string }[];
  value: { value: string; label: string };
  name: string;
  info?: string;
}

export function FontInput({ onChange, options, value, name, info }: FontInputProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { customFonts, addCustomFont } = useStyleContext();
  const [fontOptionsState, setFontOptionsState] = useState([]);

  const defaultFontOptions = [
    { id: 'Arial, sans-serif', label: 'Arial' },
    { id: 'Georgia, serif', label: 'Georgia' },
  ];

  useEffect(() => {
    const fetchCustomFontOptions = async () => {
      const infoObject = (await getInfoObject()) as any;
      const customFontOptions = infoObject?.style?.customFontFaces;
      const fontOptionsInDropdownFormat = customFontOptions?.map((font: any) => ({
        id: font.family,
        label: font.family,
      }));
      setFontOptionsState(fontOptionsInDropdownFormat);
    };
    fetchCustomFontOptions();
  }, []);

  // Convert custom fonts to dropdown format
  const customFontOptions = customFonts.map((font) => ({
    id: font.value,
    label: font.label,
  }));

  const fontOptions =
    options && options.length > 0
      ? [
          ...options.map((option) => ({
            id: option.value,
            label: option.label,
          })),
          ...(fontOptionsState || []),
          ...(customFontOptions || []),
        ]
      : [...defaultFontOptions, ...(fontOptionsState || []), ...(customFontOptions || [])];

  const optionsGroups = [{ label: 'Available Fonts', options: fontOptions }];

  // Create sticky footer component for "Add Font"
  const AddFontFooter = () => (
    <div className={styles.addFontFooter} onClick={openModal}>
      <Typography variant="p" color={Colors.accent}>
        Add Font
      </Typography>
    </div>
  );

  const handleChange = (data: SelectOption | SelectOption[]) => {
    const selectedOption = Array.isArray(data) ? data[0] : data;
    if (selectedOption) {
      onChange(selectedOption.id);
    }
  };

  const handleAddFont = (fontData: {
    value: string;
    label: string;
    name: string;
    link: string;
    weight: string;
    style: string;
  }) => {
    const customFont: CustomFont = {
      name: fontData.name,
      link: fontData.link,
      weight: fontData.weight,
      style: fontData.style,
      value: fontData.value,
      label: fontData.label,
    };

    addCustomFont(customFont);
    onChange(fontData.value);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Find selected option for the dropdown - handle undefined/null value gracefully
  const selectedOption =
    value && value.value ? fontOptions.find((option) => option.id === value.value) : null;

  return (
    <>
      <Horizontal className={styles.fontInputWrapper}>
        <InputLabel name={name} info={info} />
        <Horizontal className={styles.fontInputActions}>
          <Horizontal className={styles.fontInputWrapper}>
            <Select.Dropdown
              id={name}
              onChange={handleChange}
              selectedOptions={selectedOption ? [selectedOption.id] : []}
              optionsGroups={optionsGroups}
              dropdownWidth={'defaultWidth'}
              placeholder={selectedOption ? selectedOption.label : 'Select'}
              stickyFooter={true}
              classList={{
                selectBodyClass: styles.customDropdownBody,
                selectHeadClass: styles.fullWidthDropdownHead,
              }}
              footer={<AddFontFooter />}
            />
          </Horizontal>
        </Horizontal>
      </Horizontal>
      <AddFontModal isOpen={isModalOpen} onClose={closeModal} onAddFont={handleAddFont} />
    </>
  );
}
